import { api } from './client';
import { notifyNoteChanged } from '../utils/noteChanged';

function saved(storyId, request) {
  return Promise.resolve(request).then((result) => {
    notifyNoteChanged(storyId);
    return result;
  });
}

export const storyBibleApi = {
  get: (storyId, options = {}) => api.get(`/stories/${storyId}/bible`, {
    ...options,
    cache: 'no-store',
  }),
  refresh: (storyId) => api.post(`/stories/${storyId}/bible/refresh`),
  ensure: (storyId) => api.post(`/stories/${storyId}/bible/ensure`),
  suggestTitle: (storyId, description) =>
    api.post(`/stories/${storyId}/bible/suggest-title`, { description }),
  // Add to Bible: segment one message into proposed cards (drafts, not persisted)…
  segmentMessage: (storyId, content) =>
    api.post(`/stories/${storyId}/bible/segment-message`, { content }),
  // …then persist the approved cards as confirmed canon in an "Unsorted" section.
  // skipExistingNames (Find your cast only): leave out names already saved.
  addCards: (storyId, cards, sourceRef = null, skipExistingNames = false) =>
    saved(storyId, api.post(`/stories/${storyId}/bible/add-cards`, {
      cards,
      source_ref: sourceRef,
      ...(skipExistingNames ? { skip_existing_names: true } : {}),
    })),
  confirmEntry: (storyId, entryId) =>
    saved(storyId, api.patch(`/stories/${storyId}/bible/entries/${entryId}/confirm`)),
  updateEntry: (storyId, entryId, data) =>
    saved(storyId, api.patch(`/stories/${storyId}/bible/entries/${entryId}`, data)),
  deleteEntry: (storyId, entryId) =>
    saved(storyId, api.delete(`/stories/${storyId}/bible/entries/${entryId}`)),
  addEntry: (storyId, sectionId, data) =>
    saved(storyId, api.post(`/stories/${storyId}/bible/sections/${sectionId}/entries`, data)),
  addSection: (storyId, title) =>
    api.post(`/stories/${storyId}/bible/sections`, { title }),
  renameSection: (storyId, sectionId, title) =>
    api.patch(`/stories/${storyId}/bible/sections/${sectionId}`, { title }),
  deleteSection: (storyId, sectionId) =>
    saved(storyId, api.delete(`/stories/${storyId}/bible/sections/${sectionId}`)),
  moveEntry: (storyId, entryId, targetSectionId) =>
    api.patch(`/stories/${storyId}/bible/entries/${entryId}/move`, { target_section_id: targetSectionId }),
  suggestNames: (storyId, entryId) =>
    api.post(`/stories/${storyId}/bible/entries/${entryId}/suggest-names`),
  // Save-from-chat (dossier-consolidation) — attach a message excerpt to an
  // existing character entry as a note. No inline character creation.
  addNote: (storyId, entryId, content, sourceMessageId = null, sourceSessionId = null) =>
    saved(storyId, api.post(`/stories/${storyId}/bible/entries/${entryId}/notes`, {
      content, source_message_id: sourceMessageId, source_session_id: sourceSessionId,
    })),
  listNotes: (storyId, entryId) =>
    api.get(`/stories/${storyId}/bible/entries/${entryId}/notes`),
  listHistory: (storyId, entryId) =>
    api.get(`/stories/${storyId}/bible/entries/${entryId}/history`),
  listDatedLines: (storyId, entryId) =>
    api.get(`/stories/${storyId}/bible/entries/${entryId}/dated-lines`),
  createDatedLine: (storyId, entryId, body) =>
    api.post(`/stories/${storyId}/bible/entries/${entryId}/dated-lines`, body),
  updateDatedLine: (storyId, entryId, lineId, body) =>
    api.patch(`/stories/${storyId}/bible/entries/${entryId}/dated-lines/${lineId}`, body),
  deleteDatedLine: (storyId, entryId, lineId) =>
    api.delete(`/stories/${storyId}/bible/entries/${entryId}/dated-lines/${lineId}`),
  // Book Sheet Cards (Increment 1) — propose-only generation (writes
  // nothing; approval is addCards/updateEntry above) and the pre-creation
  // character candidate scan for the "New Character" naming wizard.
  redraft: (storyId, entryId) => saved(
    storyId,
    api.post(`/stories/${storyId}/bible/entries/${entryId}/redraft`, {}),
  ),
  generateCard: (storyId, {
    category,
    instanceName = null,
    nickname = null,
    alsoKnownAs = null,
    seedText = null,
    descriptionStyle = null,
    generateBoth = false,
  }) =>
    api.post(`/stories/${storyId}/bible/generate-card`, {
      category, instance_name: instanceName, seed_text: seedText,
      nickname: nickname || undefined,
      also_known_as: (alsoKnownAs && alsoKnownAs.length) ? alsoKnownAs : undefined,
      description_style: descriptionStyle,
      generate_both: generateBoth || undefined,
    }),
  detectCharacters: (storyId) =>
    api.post(`/stories/${storyId}/bible/detect-characters`),
  // Book Sheet Cards (Increment 2) — the same pre-creation detect-and-pick
  // pattern as detectCharacters above, for the "New Setting/Location" wizard.
  detectLocations: (storyId) =>
    api.post(`/stories/${storyId}/bible/detect-locations`),
};
